import { faker } from '@faker-js/faker';

// Helper functions for array operations
const arrayElement = <T>(array: T[]): T => array[Math.floor(Math.random() * array.length)];
const arrayElements = <T>(array: T[], options: { min?: number; max?: number } = {}): T[] => {
  const { min = 1, max = array.length } = options;
  const count = Math.floor(Math.random() * (max - min + 1)) + min;
  const shuffled = [...array].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};
const maybe = <T>(fn: () => T, options: { probability?: number } = {}): T | undefined => {
  const { probability = 0.5 } = options;
  return Math.random() < probability ? fn() : undefined;
};

// Generate users with Faker
export const generateUsers = (count: number = 50) => {
  return Array.from({ length: count }, (_, index) => ({
    id: `user_${String(index + 1).padStart(3, '0')}`,
    name: faker.person.fullName(),
    email: faker.internet.email(),
    mobile: faker.phone.number(),
    role: index === 0 ? 'Admin' : arrayElement(['Customer', 'Manager']),
    status: arrayElement(['Active', 'Inactive', 'Suspended']),
    joinDate: faker.date.past({ years: 2 }).toISOString().split('T')[0],
    lastLogin: faker.date.recent().toISOString(),
    totalBookings: faker.number.int({ min: 0, max: 50 }),
    totalSpent: faker.number.int({ min: 0, max: 25000 }),
    address: `${faker.location.streetAddress()}, ${faker.location.city()}, Kerala`,
    gender: faker.person.gender(),
    dateOfBirth: faker.date.birthdate({ min: 18, max: 65, mode: 'age' }).toISOString().split('T')[0],
    profilePicture: faker.image.avatar(),
    isVerified: Math.random() < 0.8, // 80% verified
    preferences: {
      genrePreferences: arrayElements([
        'Action', 'Comedy', 'Drama', 'Horror', 'Romance', 'Thriller', 'Sci-Fi', 'Adventure'
      ], { min: 2, max: 5 }),
      language: arrayElement(['English', 'Malayalam', 'Hindi', 'Tamil']),
      preferredCinema: arrayElement(['MyCinemas Angamaly', 'MyCinemas Kochi'])
    }
  }));
};

// Generate movies with Faker
export const generateMovies = (count: number = 25) => {
  const genres = [
    'Action', 'Adventure', 'Animation', 'Comedy', 'Crime', 'Documentary',
    'Drama', 'Family', 'Fantasy', 'Horror', 'Musical', 'Mystery',
    'Romance', 'Sci-Fi', 'Thriller', 'War', 'Western'
  ];

  return Array.from({ length: count }, (_, index) => {
    const selectedGenres = arrayElements(genres, { min: 2, max: 4 });

    return {
      id: `movie_${String(index + 1).padStart(3, '0')}`,
      vistaMovieId: `vista-${String(index + 1).padStart(4, '0')}`,
      title: arrayElement([
        'Avengers: Endgame', 'Spider-Man: No Way Home', 'Dune', 'No Time to Die',
        'Eternals', 'The Batman', 'Black Widow', 'Shang-Chi', 'Venom: Let There Be Carnage',
        'Fast & Furious 9', 'A Quiet Place Part II', 'Godzilla vs. Kong', 'Mortal Kombat',
        'Cruella', 'Luca', 'Raya and the Last Dragon', 'Soul', 'Onward', 'Frozen II',
        'Toy Story 4', 'The Lion King', 'Aladdin', 'Captain Marvel', 'Aquaman',
        'Wonder Woman 1984', 'Joker', 'Once Upon a Time in Hollywood'
      ]),
      tagline: faker.lorem.sentence(),
      description: faker.lorem.paragraphs(2),
      genres: selectedGenres,
      genre: selectedGenres.join(', '),
      duration: faker.number.int({ min: 90, max: 200 }),
      rating: arrayElement(['U', 'U/A', 'A', 'S']),
      language: arrayElement(['English', 'Malayalam', 'Hindi', 'Tamil', 'Telugu']),
      releaseDate: faker.date.past({ years: 3 }).toISOString().split('T')[0],
      posterUrl: faker.image.url({ width: 300, height: 450 }),
      bannerUrl: faker.image.url({ width: 1280, height: 720 }),
      trailerUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      cast: Array.from({ length: faker.number.int({ min: 3, max: 8 }) }, () => 
        faker.person.fullName()
      ).join(', '),
      director: faker.person.fullName(),
      status: arrayElement(['Now Showing', 'Coming Soon', 'Ended']),
      totalBookings: faker.number.int({ min: 50, max: 500 }),
      revenue: faker.number.int({ min: 25000, max: 250000 }),
      isActive: Math.random() < 0.9, // 90% active
      createdAt: faker.date.past({ years: 1 }),
      updatedAt: faker.date.recent()
    };
  });
};

// Generate cinemas (Angamaly and Kochi)
export const generateCinemas = () => {
  return [
    {
      id: 'cinema_001',
      name: 'MyCinemas Angamaly',
      location: 'Angamaly',
      address: 'NH 544, Angamaly, Kerala 683572',
      city: 'Angamaly',
      state: 'Kerala',
      pincode: '683572',
      phone: '+91-484-2345678',
      email: 'angamaly@mycinemas.com',
      latitude: 10.1964,
      longitude: 76.3875,
      screens: 4,
      totalSeats: 630,
      amenities: [
        'Dolby Atmos Sound', 'Recliner Seats', 'Food Court', 'Parking',
        'Wheelchair Accessible', 'Online Booking', 'Loyalty Program'
      ],
      status: 'Active',
      totalBookings: faker.number.int({ min: 200, max: 800 }),
      revenue: faker.number.int({ min: 100000, max: 400000 }),
      openingDate: '2020-01-15',
      manager: faker.person.fullName(),
      capacity: 630,
      screensInfo: [
        { id: 'screen_001', name: 'Screen 1 - Dolby Atmos', capacity: 200, type: 'Standard' },
        { id: 'screen_002', name: 'Screen 2 - Premium', capacity: 150, type: 'Premium' },
        { id: 'screen_003', name: 'Screen 3 - Standard', capacity: 180, type: 'Standard' },
        { id: 'screen_004', name: 'Screen 4 - VIP', capacity: 100, type: 'VIP' }
      ]
    },
    {
      id: 'cinema_002',
      name: 'MyCinemas Kochi',
      location: 'Kochi',
      address: 'Marine Drive, Kochi, Kerala 682001',
      city: 'Kochi',
      state: 'Kerala',
      pincode: '682001',
      phone: '+91-484-3456789',
      email: 'kochi@mycinemas.com',
      latitude: 9.9312,
      longitude: 76.2673,
      screens: 6,
      totalSeats: 1180,
      amenities: [
        'IMAX Screen', 'Dolby Atmos Sound', 'Recliner Seats', 'Food Court',
        'Parking', 'Wheelchair Accessible', 'Online Booking', 'Loyalty Program',
        'Valet Parking', 'Premium Lounge'
      ],
      status: 'Active',
      totalBookings: faker.number.int({ min: 400, max: 1200 }),
      revenue: faker.number.int({ min: 200000, max: 600000 }),
      openingDate: '2019-06-20',
      manager: faker.person.fullName(),
      capacity: 1180,
      screensInfo: [
        { id: 'screen_005', name: 'IMAX Screen', capacity: 300, type: 'IMAX' },
        { id: 'screen_006', name: 'Screen 2 - Dolby Atmos', capacity: 220, type: 'Premium' },
        { id: 'screen_007', name: 'Screen 3 - Standard', capacity: 200, type: 'Standard' },
        { id: 'screen_008', name: 'Screen 4 - VIP', capacity: 120, type: 'VIP' },
        { id: 'screen_009', name: 'Screen 5 - Premium', capacity: 180, type: 'Premium' },
        { id: 'screen_010', name: 'Screen 6 - Standard', capacity: 160, type: 'Standard' }
      ]
    }
  ];
};

// Generate bookings with Faker
export const generateBookings = (count: number = 100) => {
  const movies = generateMovies(10);
  const cinemas = generateCinemas();
  const users = generateUsers(20);

  return Array.from({ length: count }, (_, index) => {
    const movie = arrayElement(movies);
    const cinema = arrayElement(cinemas);
    const user = arrayElement(users);
    const showDate = faker.date.future();
    const bookingDate = faker.date.past();
    
    return {
      id: `BK${String(index + 1).padStart(6, '0')}`,
      customer: user.name,
      customerId: user.id,
      customerEmail: user.email,
      customerMobile: user.mobile,
      movie: movie.title,
      movieId: movie.id,
      cinema: cinema.name,
      cinemaId: cinema.id,
      showDate: showDate.toISOString().split('T')[0],
      showTime: arrayElement(['10:00', '13:30', '16:00', '18:30', '21:00', '23:30']),
      seats: Array.from({ length: faker.number.int({ min: 1, max: 4 }) }, () => 
        `${arrayElement(['A', 'B', 'C', 'D', 'E', 'F'])}${faker.number.int({ min: 1, max: 20 })}`
      ),
      amount: `₹${faker.number.int({ min: 300, max: 2000 })}`,
      status: arrayElement(['confirmed', 'pending', 'cancelled', 'completed']),
      bookingDate: bookingDate.toISOString(),
      paymentStatus: arrayElement(['completed', 'pending', 'failed', 'refunded']),
      paymentMethod: arrayElement(['Credit Card', 'Debit Card', 'UPI', 'Net Banking', 'Wallet']),
      deviceInfo: {
        userAgent: faker.internet.userAgent(),
        platform: arrayElement(['iOS', 'Android', 'Windows', 'macOS']),
        ipAddress: faker.internet.ip()
      },
      bookingSource: arrayElement(['web', 'mobile_app', 'api']),
      cancellationReason: maybe(() => arrayElement([
        'Change of plans', 'Emergency', 'Technical issues', 'Not satisfied with seats'
      ]), { probability: 0.2 })
    };
  });
};

// Generate pages with Faker
export const generatePages = () => {
  const pageTypes = [
    { title: 'About Us', slug: 'about-us', category: 'Company' },
    { title: 'Terms and Conditions', slug: 'terms-conditions', category: 'Legal' },
    { title: 'Privacy Policy', slug: 'privacy-policy', category: 'Legal' },
    { title: 'Contact Us', slug: 'contact', category: 'Company' },
    { title: 'FAQ', slug: 'faq', category: 'Support' },
    { title: 'Careers', slug: 'careers', category: 'Company' },
    { title: 'Press Release', slug: 'press', category: 'Media' },
    { title: 'Investor Relations', slug: 'investors', category: 'Company' }
  ];

  return pageTypes.map((page, index) => ({
    id: `page_${String(index + 1).padStart(3, '0')}`,
    title: page.title,
    slug: page.slug,
    category: page.category,
    status: arrayElement(['Published', 'Draft', 'Archived']),
    lastModified: faker.date.recent().toISOString(),
    views: faker.number.int({ min: 100, max: 5000 }),
    author: faker.person.fullName(),
    content: faker.lorem.paragraphs(faker.number.int({ min: 3, max: 8 })),
    metaTitle: `${page.title} - MyCinemas`,
    metaDescription: faker.lorem.sentence(),
    isPublished: Math.random() < 0.8
  }));
};

// Generate banners with Faker
export const generateBanners = (count: number = 8) => {
  const positions = ['Hero', 'Featured', 'Promotional', 'Sidebar'];
  const movies = generateMovies(10);

  return Array.from({ length: count }, (_, index) => {
    const movie = arrayElement(movies);
    
    return {
      id: `banner_${String(index + 1).padStart(3, '0')}`,
      title: arrayElement([
        `${movie.title} - Now Showing`,
        `${movie.title} - Coming Soon`,
        'Special Offer - 20% Off',
        'Weekend Special - Buy 2 Get 1 Free',
        'Student Discount - 15% Off',
        'Loyalty Program - Earn Points',
        'New Year Special - 25% Off',
        'Valentine\'s Day Special'
      ]),
      description: faker.lorem.sentence(),
      position: arrayElement(positions),
      status: arrayElement(['Active', 'Inactive', 'Scheduled']),
      clicks: faker.number.int({ min: 50, max: 5000 }),
      impressions: faker.number.int({ min: 1000, max: 50000 }),
      startDate: faker.date.past().toISOString().split('T')[0],
      endDate: faker.date.future().toISOString().split('T')[0],
      imageUrl: faker.image.url({ width: 1200, height: 400 }),
      linkUrl: faker.internet.url(),
      priority: arrayElement(['High', 'Medium', 'Low']),
      targetAudience: arrayElement(['All', 'Students', 'Families', 'Adults']),
      createdBy: faker.person.fullName(),
      createdAt: faker.date.past()
    };
  });
};

// Generate notifications with Faker
export const generateNotifications = (count: number = 50) => {
  const types = ['email', 'sms', 'push'];
  const statuses = ['sent', 'delivered', 'failed', 'pending'];
  const templates = [
    'Booking Confirmation', 'Payment Reminder', 'Show Reminder', 
    'Booking Cancellation', 'Welcome Message', 'Promotional Offer'
  ];

  return Array.from({ length: count }, (_, index) => ({
    id: `notif_${String(index + 1).padStart(3, '0')}`,
    type: arrayElement(types),
    recipient: arrayElement([
      faker.internet.email(),
      faker.phone.number(),
      faker.string.uuid()
    ]),
    subject: maybe(() => faker.lorem.sentence(), { probability: 0.7 }),
    content: faker.lorem.paragraph(),
    status: arrayElement(statuses),
    sentAt: faker.date.recent().toISOString(),
    deliveredAt: maybe(() => faker.date.recent().toISOString(), { probability: 0.8 }),
    template: arrayElement(templates),
    priority: arrayElement(['Low', 'Normal', 'High', 'Urgent']),
    errorMessage: maybe(() => faker.lorem.sentence(), { probability: 0.1 }),
    retryCount: faker.number.int({ min: 0, max: 3 }),
    createdAt: faker.date.past()
  }));
};

// Generate templates with Faker
export const generateTemplates = (count: number = 15) => {
  const types = ['email', 'sms', 'push'];
  const categories = ['booking', 'payment', 'reminder', 'promotional', 'system'];

  return Array.from({ length: count }, (_, index) => ({
    id: `template_${String(index + 1).padStart(3, '0')}`,
    name: arrayElement([
      'Booking Confirmation', 'Payment Reminder', 'Show Reminder',
      'Booking Cancellation', 'Welcome Message', 'Promotional Offer',
      'Password Reset', 'Account Verification', 'Loyalty Points Update',
      'Special Offer', 'Movie Recommendation', 'Feedback Request',
      'System Maintenance', 'New Feature Announcement', 'Holiday Greeting'
    ]),
    type: arrayElement(types),
    category: arrayElement(categories),
    subject: maybe(() => faker.lorem.sentence(), { probability: 0.8 }),
    content: faker.lorem.paragraphs(faker.number.int({ min: 1, max: 3 })),
    variables: arrayElements([
      'userName', 'movieTitle', 'showTime', 'cinemaName', 'bookingId',
      'amount', 'seatNumbers', 'date', 'time', 'phone', 'email'
    ], { min: 2, max: 6 }),
    isActive: Math.random() < 0.9,
    lastUsed: maybe(() => faker.date.recent().toISOString(), { probability: 0.7 }),
    usageCount: faker.number.int({ min: 0, max: 500 }),
    createdBy: faker.person.fullName(),
    createdAt: faker.date.past(),
    updatedAt: faker.date.recent()
  }));
};

// Generate media files with Faker
export const generateMedia = (count: number = 30) => {
  const mediaTypes = ['image', 'video', 'document', 'audio'];
  const categories = ['Movies', 'Events', 'Promotions', 'Gallery', 'Documents', 'Videos'];
  
  return Array.from({ length: count }, (_, index) => ({
    id: `media_${String(index + 1).padStart(3, '0')}`,
    filename: arrayElement([
      'movie-poster-avengers.jpg',
      'cinema-interior.jpg',
      'promotional-banner.png',
      'event-photo.jpg',
      'movie-trailer.mp4',
      'cinema-lobby.jpg',
      'special-offer.png',
      'behind-scenes.jpg',
      'movie-still.jpg',
      'cinema-exterior.jpg',
      'popcorn-combo.jpg',
      'seating-arrangement.jpg',
      'dolby-atmos.jpg',
      'imax-screen.jpg',
      'vip-lounge.jpg'
    ]),
    originalName: arrayElement([
      'Avengers Endgame Poster',
      'Cinema Interior Shot',
      'Promotional Banner Design',
      'Event Photography',
      'Movie Trailer Video',
      'Cinema Lobby View',
      'Special Offer Graphic',
      'Behind the Scenes',
      'Movie Still Frame',
      'Cinema Exterior View',
      'Popcorn Combo Image',
      'Seating Arrangement',
      'Dolby Atmos Setup',
      'IMAX Screen View',
      'VIP Lounge Interior'
    ]),
    type: arrayElement(mediaTypes),
    category: arrayElement(categories),
    size: faker.number.int({ min: 100000, max: 50000000 }), // bytes
    mimeType: arrayElement([
      'image/jpeg',
      'image/png',
      'image/gif',
      'video/mp4',
      'video/avi',
      'application/pdf',
      'audio/mp3',
      'audio/wav'
    ]),
    url: faker.image.url({ width: 800, height: 600 }),
    thumbnailUrl: faker.image.url({ width: 200, height: 150 }),
    alt: faker.lorem.sentence(),
    description: faker.lorem.paragraph(),
    tags: arrayElements([
      'movie', 'cinema', 'promotion', 'event', 'poster', 'banner', 'trailer',
      'interior', 'exterior', 'food', 'seating', 'technology', 'vip', 'imax'
    ], { min: 2, max: 5 }),
    movieId: Math.random() < 0.6 ? `movie_${String(faker.number.int({ min: 1, max: 25 })).padStart(3, '0')}` : null,
    isPublic: Math.random() < 0.8,
    isFeatured: Math.random() < 0.3,
    uploadedBy: faker.person.fullName(),
    uploadedAt: faker.date.past(),
    lastModified: faker.date.recent(),
    downloadCount: faker.number.int({ min: 0, max: 1000 }),
    viewCount: faker.number.int({ min: 0, max: 5000 })
  }));
};

// Generate analytics data
export const generateAnalytics = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  return {
    revenueData: months.map(month => ({
      month,
      revenue: faker.number.int({ min: 20000, max: 80000 }),
      bookings: faker.number.int({ min: 100, max: 400 }),
      users: faker.number.int({ min: 50, max: 200 })
    })),
    movieData: generateMovies(10).map(movie => ({
      name: movie.title,
      value: movie.totalBookings,
      color: faker.color.rgb()
    })),
    cinemaData: generateCinemas().map(cinema => ({
      name: cinema.name,
      bookings: cinema.totalBookings,
      revenue: cinema.revenue,
      occupancy: faker.number.int({ min: 60, max: 95 })
    })),
    userGrowth: months.map(month => ({
      month,
      newUsers: faker.number.int({ min: 20, max: 100 }),
      activeUsers: faker.number.int({ min: 100, max: 500 })
    }))
  };
};

// Export all generated data
export const mockData = {
  users: generateUsers(50),
  movies: generateMovies(25),
  cinemas: generateCinemas(),
  bookings: generateBookings(100),
  pages: generatePages(),
  banners: generateBanners(8),
  media: generateMedia(30),
  notifications: generateNotifications(50),
  templates: generateTemplates(15),
  analytics: generateAnalytics()
};
